'use client'

import type {
  A11yContextProps,
  AnnouncementItem,
} from '@maestro/core/a11y/types'

import { usePathname } from 'next/navigation'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

const A11yContext = createContext<A11yContextProps | undefined>(undefined)

export const A11yProvider = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname()
  const isInitialMount = useRef(true)
  const [politeQueue, setPoliteQueue] = useState<AnnouncementItem[]>([])
  const [assertiveQueue, setAssertiveQueue] = useState<AnnouncementItem[]>([])
  const [politeCurrent, setPoliteCurrent] = useState<AnnouncementItem | null>(
    null
  )
  const [assertivecurrent, setAssertiveCurrent] =
    useState<AnnouncementItem | null>(null)
  const idCounter = useRef(0)
  const politeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const assertiveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const resolversRef = useRef<Map<string, () => void>>(new Map())

  const announce = useCallback(
    (message: string, priority: 'polite' | 'assertive' = 'polite') => {
      if (!message.trim()) {
        return Promise.resolve()
      }

      const id = `announcement-${++idCounter.current}`
      const announcement: AnnouncementItem = {
        id,
        message,
        priority,
      }

      const promise = new Promise<void>((resolve) => {
        resolversRef.current.set(id, resolve)
      })

      if (priority === 'assertive') {
        setAssertiveQueue((prev) => [...prev, announcement])
      } else {
        setPoliteQueue((prev) => [...prev, announcement])
      }

      return promise
    },
    []
  )

  const clearAnnouncements = useCallback(() => {
    setPoliteQueue([])
    setAssertiveQueue([])
    setPoliteCurrent(null)
    setAssertiveCurrent(null)
    if (politeTimeoutRef.current) {
      clearTimeout(politeTimeoutRef.current)
    }
    if (assertiveTimeoutRef.current) {
      clearTimeout(assertiveTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (!politeCurrent && politeQueue.length > 0) {
      const [next, ...rest] = politeQueue
      setPoliteQueue(rest)

      // Set to null first to trigger change detection
      setPoliteCurrent(null)
      setTimeout(() => {
        setPoliteCurrent(next)

        // Resolve promise after a short delay to allow focus management
        setTimeout(() => {
          const resolver = resolversRef.current.get(next.id)
          if (resolver) {
            resolver()
            resolversRef.current.delete(next.id)
          }
        }, 200)

        // Calculate reading time and clear current after reading
        const readTime = Math.max(2000, next.message.length * 60)
        politeTimeoutRef.current = setTimeout(() => {
          setPoliteCurrent(null)
          politeTimeoutRef.current = null
        }, readTime)
      }, 50)
    }
  }, [politeCurrent, politeQueue])

  useEffect(() => {
    return () => {
      if (politeTimeoutRef.current) {
        clearTimeout(politeTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!assertivecurrent && assertiveQueue.length > 0) {
      const [next, ...rest] = assertiveQueue
      setAssertiveQueue(rest)

      setAssertiveCurrent(null)
      setTimeout(() => {
        setAssertiveCurrent(next)

        // Resolve promise after a short delay to allow focus management
        setTimeout(() => {
          const resolver = resolversRef.current.get(next.id)
          if (resolver) {
            resolver()
            resolversRef.current.delete(next.id)
          }
        }, 200)

        // Calculate reading time and clear current after reading
        const readTime = Math.max(2000, next.message.length * 60)
        assertiveTimeoutRef.current = setTimeout(() => {
          setAssertiveCurrent(null)
          assertiveTimeoutRef.current = null
        }, readTime)
      }, 50)
    }
  }, [assertivecurrent, assertiveQueue])

  useEffect(() => {
    return () => {
      if (assertiveTimeoutRef.current) {
        clearTimeout(assertiveTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Reset focus to body after route change
    const resetFocus = () => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }

      // Focus the body with tabindex to ensure next Tab goes to first element
      document.body.setAttribute('tabindex', '-1')
      document.body.focus({ preventScroll: true })
      document.body.removeAttribute('tabindex')
    }

    requestAnimationFrame(() => {
      setTimeout(resetFocus, 50)
    })
  }, [pathname])

  return (
    <A11yContext.Provider value={{ announce, clearAnnouncements }}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        aria-relevant="additions text"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
        role="status"
      >
        {politeCurrent?.message || ''}
      </div>
      <div
        aria-live="assertive"
        aria-atomic="true"
        aria-relevant="additions text"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
        role="alert"
      >
        {assertivecurrent?.message || ''}
      </div>
    </A11yContext.Provider>
  )
}

export const useA11y = () => {
  const context = useContext(A11yContext)
  if (!context) {
    throw new Error('useA11y must be used within an A11yProvider')
  }
  return context
}
